// "Flight text quote" generator — Additive to the existing WhatsApp Path B
// (wa.me + mark-as-sent), no new send mechanism. Reuses the segment parser
// already built for itinerary.flightDetails (GdsFlightCard.js) so only lines
// it recognises as a real flight segment ever get forwarded — any other
// pasted line (fare, commission, notes) is silently dropped by that parser,
// never reaching this output.
import { parseGdsFlights, airportName, knownAirlineName } from '@/components/itinerary/GdsFlightCard';

const fmtDur = (mins) => {
  if (mins == null || mins <= 0) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const parts = [];
  if (h) parts.push(`${h}h`);
  if (m || !h) parts.push(`${String(m).padStart(2, '0')}m`);
  return parts.join(' ');
};

// *ORIGIN - STOP - ORIGIN* header from the parsed waypoints, de-duplicating
// consecutive repeats (a connecting-flight handover airport isn't listed twice).
export function buildFlightHeader(directions) {
  const path = [];
  directions.forEach((dir) => {
    dir.legs.forEach((leg, i) => {
      if (path.length === 0) path.push(leg.origin);
      path.push(leg.dest);
    });
  });
  const deduped = path.filter((code, i) => i === 0 || code !== path[i - 1]);
  return deduped.map((code) => (airportName(code) || code).toUpperCase()).join(' - ');
}

// One [route line, duration line] pair per recognised leg. Duration line is
// OMITTED (never guessed) when the leg's airport time zone is unknown —
// caller surfaces `warnings` to the agent instead. Terminal shown in
// parentheses after an airport code only when that leg actually carries it.
export function buildFlightSegmentLines(directions) {
  const lines = [];
  const warnings = [];
  directions.forEach((dir) => {
    dir.legs.forEach((leg) => {
      const origin = `${leg.origin}${leg.originTerminal ? ` (${leg.originTerminal})` : ''}`;
      const dest = `${leg.dest}${leg.destTerminal ? ` (${leg.destTerminal})` : ''}`;
      const arr = `${leg.arrTime}${leg.arrDayOffset > 0 ? `(+${leg.arrDayOffset})` : ''}`;
      lines.push(`${leg.date} ${leg.flightLabel} ${origin} ${dest} ${leg.depTime} ${arr}`);
      if (leg.durationUnknown) {
        warnings.push(
          leg.missingTzCode
            ? `Duration omitted for ${leg.origin}-${leg.dest} — unknown airport code "${leg.missingTzCode}". Please add it manually if needed.`
            : `Duration omitted for ${leg.origin}-${leg.dest} — could not be computed. Please add it manually if needed.`
        );
      } else {
        const dur = fmtDur(leg.durationMins);
        if (dur) lines.push(`${dur}${leg.arrDayOffset > 0 ? `(+${leg.arrDayOffset}D)` : ''}`);
      }
    });
  });
  return { lines, warnings };
}

// "Note : Airline" block — Onward = first direction's first leg, Return =
// last direction's first leg (round-trip labels, per requirement). Blank
// value when the leg's airline code isn't in the known-names map — never
// guessed.
export function buildAirlineNoteLines(directions) {
  if (directions.length === 0) return [];
  const nameFor = (dir) => knownAirlineName((dir?.legs || [])[0]?.airline || '');
  const lines = ['Note : Airline', `Onward : ${nameFor(directions[0])}`];
  if (directions.length > 1) lines.push(`Return : ${nameFor(directions[directions.length - 1])}`);
  return lines;
}

/**
 * farePerPerson MUST already be computed by the caller from the selected
 * pricing option's total ÷ traveller count — this function never reads a
 * fare out of the pasted flightDetails text. fareRules is an array of
 * agent-typed lines (repeatable list) — each rendered on its own line.
 */
export function buildFlightTextQuote({ flightDetails, farePerPerson, seatsLeft, fareRules }) {
  const directions = parseGdsFlights(flightDetails || '');
  if (directions.length === 0) return { text: '', hasSegments: false, directions, warnings: [] };

  const { lines: segLines, warnings } = buildFlightSegmentLines(directions);
  const lines = [`*${buildFlightHeader(directions)}*`, ...segLines];
  const seats = String(seatsLeft || '').trim();
  if (seats) lines.push(`${seats} seat(s) left`);
  lines.push(`Fare : ₹${Math.round(Number(farePerPerson) || 0).toLocaleString('en-IN')} Per Person`);
  (Array.isArray(fareRules) ? fareRules : [fareRules]).forEach((r) => {
    const rule = String(r || '').trim();
    if (rule) lines.push(rule);
  });
  lines.push(...buildAirlineNoteLines(directions));

  return { text: lines.join('\n'), hasSegments: true, directions, warnings };
}
